import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { ELEMENT_IDS } from "features/bookmarks/utils";

import { VIEWER_INSET } from "./constants";
import { calculateWindowPosition, type WindowPosition } from "./windowPositioning";

type Size = { width: number; height: number };
type Position = { x: number; y: number };

const DEFAULT_SIZE: Size = { width: 352, height: 645 };
const DEFAULT_MIN_SIZE: Required<Size> = { width: 352, height: 625 };

function horizontalAnchorFromEdgeGaps(distFromLeftInset: number, rightGap: number): "left" | "right" {
    return distFromLeftInset <= rightGap ? "left" : "right";
}

function verticalAnchorFromEdgeGaps(distFromTopInset: number, bottomGap: number): "top" | "bottom" {
    return distFromTopInset <= bottomGap ? "top" : "bottom";
}

function computeEdgeMetrics(pos: Position, containerSize: Size, windowSize: Size) {
    const rightGap = containerSize.width - VIEWER_INSET - windowSize.width - pos.x;
    const bottomGap = containerSize.height - VIEWER_INSET - windowSize.height - pos.y;
    return {
        rightGap,
        bottomGap,
        horizontalAnchor: horizontalAnchorFromEdgeGaps(pos.x - VIEWER_INSET, rightGap),
        verticalAnchor: verticalAnchorFromEdgeGaps(pos.y - VIEWER_INSET, bottomGap),
    };
}

function getRenderContainerRect(): { left: number; top: number; width: number; height: number } {
    const container = document.getElementById(ELEMENT_IDS.renderContainer);
    if (container) {
        const r = container.getBoundingClientRect();
        return { left: r.left, top: r.top, width: r.width, height: r.height };
    }
    return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
}

/** Minimal subset of `react-rnd`'s `Rnd` instance shape used by `setRndRef`. */
type RndInstanceLike = { resizableElement: { current: HTMLElement | null } } | null;

export interface UseWindowGeometryArgs {
    position?: WindowPosition;
    /** `height` may be omitted by `autoHeight` windows; it then falls back to `minSize.height`. */
    size?: { width: number; height?: number };
    minSize?: { width?: number; height?: number };
    /** When true, height is `"auto"` (content-driven). */
    autoHeight?: boolean;
    /** When true, geometry is fully controlled by the given position/size. */
    locked?: boolean;
}

export interface UseWindowGeometryResult {
    /** Position adjusted for Rnd's inset-relative coordinate space. */
    position: Position;
    /** Size for Rnd. Height is `"auto"` when `autoHeight` is true. */
    size: { width: number; height: number | "auto" };
    minWidth: number;
    /** Undefined when auto-height is active so Rnd doesn't fight the content. */
    minHeight: number | undefined;
    onDragStart: () => void;
    onDragStop: (_: unknown, d: { x: number; y: number }) => void;
    onResizeStop: (
        _e: unknown,
        _dir: unknown,
        elementRef: HTMLElement,
        _delta: unknown,
        pos: { x: number; y: number },
    ) => void;
    /** Attach to `<Rnd>` so clamping can use the rendered height in `autoHeight` mode. */
    setRndRef: (instance: RndInstanceLike) => void;
}

/**
 * Layout for a draggable/resizable Window: initial placement, container clamping,
 * edge-anchoring across container resizes, and drag/resize handlers.
 */
export function useWindowGeometry({
    position,
    size = DEFAULT_SIZE,
    minSize,
    autoHeight = false,
    locked = false,
}: UseWindowGeometryArgs): UseWindowGeometryResult {
    const { width: minWidth = DEFAULT_MIN_SIZE.width, height: minHeight = DEFAULT_MIN_SIZE.height } = minSize ?? {};

    // `autoHeight` windows register width only. Resolve the missing height to
    // minHeight so position clamping still has a concrete footprint — without
    // this, Math.max(undefined, minHeight) yields NaN and poisons x/y.
    const effectiveSize: Size = {
        width: Math.max(size.width, minWidth),
        height: Math.max(size.height ?? minHeight, minHeight),
    };

    const isDraggingRef = useRef(false);

    const containerRect = getRenderContainerRect();
    const initialContainerSize: Size = { width: containerRect.width, height: containerRect.height };

    const windowPos = calculateWindowPosition(position, effectiveSize, containerRect);

    const [currentSize, setCurrentSize] = useState<Size>(effectiveSize);
    const currentSizeRef = useRef(currentSize);
    currentSizeRef.current = currentSize;

    // Rendered Rnd element — clamping uses its actual height (~60px when minimized).
    const elementRef = useRef<HTMLElement | null>(null);
    const setRndRef = useCallback((instance: RndInstanceLike) => {
        elementRef.current = instance?.resizableElement?.current ?? null;
    }, []);

    const getVisualSize = useCallback((): Size => {
        const stored = currentSizeRef.current;
        if (autoHeight && elementRef.current) {
            const measured = elementRef.current.offsetHeight;
            if (measured > 0) {
                return { width: stored.width, height: measured };
            }
        }
        return stored;
    }, [autoHeight]);

    const clampToContainer = useCallback(
        (x: number, y: number, containerWidth: number, containerHeight: number, windowSize: Size): Position => {
            const minX = VIEWER_INSET;
            const maxX = Math.max(minX, containerWidth - windowSize.width - VIEWER_INSET);
            const minY = VIEWER_INSET;
            const maxY = Math.max(minY, containerHeight - windowSize.height - VIEWER_INSET);
            return {
                x: Math.max(minX, Math.min(x, maxX)),
                y: Math.max(minY, Math.min(y, maxY)),
            };
        },
        [],
    );

    const initial = clampToContainer(
        windowPos.x,
        windowPos.y,
        initialContainerSize.width,
        initialContainerSize.height,
        effectiveSize,
    );
    const [currentPos, setCurrentPos] = useState<Position>(initial);

    const initialMetrics = computeEdgeMetrics(initial, initialContainerSize, effectiveSize);
    const horizontalAnchorRef = useRef<"left" | "right">(initialMetrics.horizontalAnchor);
    const verticalAnchorRef = useRef<"top" | "bottom">(initialMetrics.verticalAnchor);
    const rightGapRef = useRef(initialMetrics.rightGap);
    const bottomGapRef = useRef(initialMetrics.bottomGap);

    const getContainerSize = useCallback((): Size => {
        const container = document.getElementById(ELEMENT_IDS.renderContainer);
        if (container) {
            return { width: container.clientWidth, height: container.clientHeight };
        }
        return { width: window.innerWidth, height: window.innerHeight };
    }, []);

    // Floating drags can transition into a docked/locked window before Rnd emits
    // `onDragStop`. Reset the internal drag guard so future undock drags can
    // follow external position updates immediately.
    useEffect(() => {
        if (locked) {
            isDraggingRef.current = false;
        }
    }, [locked]);

    // Mirrors the externally-controlled `position` prop (e.g. dock drag-out
    // feeds cursor coords) into `currentPos`. Skipped while Rnd owns the drag
    // so its internal state isn't fought.
    useLayoutEffect(() => {
        if (locked || isDraggingRef.current || position == null) {
            return;
        }
        const rect = getRenderContainerRect();
        const ws = currentSizeRef.current;
        const resolved = calculateWindowPosition(position, ws, rect);
        const clamped = clampToContainer(resolved.x, resolved.y, rect.width, rect.height, ws);
        setCurrentPos((prev) => (prev.x === clamped.x && prev.y === clamped.y ? prev : clamped));
        const metrics = computeEdgeMetrics(clamped, { width: rect.width, height: rect.height }, ws);
        rightGapRef.current = metrics.rightGap;
        bottomGapRef.current = metrics.bottomGap;
        horizontalAnchorRef.current = metrics.horizontalAnchor;
        verticalAnchorRef.current = metrics.verticalAnchor;
    }, [position, clampToContainer, locked]);

    // Same for `size`: dock drag-out sets `dragSize`; clearing it on release
    // must restore the registered size. Depend on primitives — `effectiveSize`
    // is a fresh reference every render.
    useEffect(() => {
        if (locked || isDraggingRef.current) {
            return;
        }
        setCurrentSize((prev) =>
            prev.width === effectiveSize.width && prev.height === effectiveSize.height ? prev : effectiveSize,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveSize.width, effectiveSize.height, locked]);

    useEffect(() => {
        if (locked) {
            return;
        }
        const container = document.getElementById(ELEMENT_IDS.renderContainer);
        if (!container) {
            return;
        }

        const observer = new ResizeObserver(() => {
            const newW = container.clientWidth;
            const newH = container.clientHeight;

            if (isDraggingRef.current) {
                return;
            }

            flushSync(() => {
                setCurrentPos((prev) => {
                    const ws = currentSizeRef.current;
                    const newX =
                        horizontalAnchorRef.current === "left"
                            ? prev.x
                            : newW - VIEWER_INSET - ws.width - rightGapRef.current;

                    const newY =
                        verticalAnchorRef.current === "top"
                            ? prev.y
                            : newH - VIEWER_INSET - ws.height - bottomGapRef.current;

                    return clampToContainer(newX, newY, newW, newH, ws);
                });
            });
        });

        observer.observe(container);
        return () => observer.disconnect();
    }, [clampToContainer, locked]);

    const onDragStart = useCallback(() => {
        if (locked) {
            return;
        }
        isDraggingRef.current = true;
    }, [locked]);

    const onDragStop = useCallback(
        (_: unknown, d: { x: number; y: number }) => {
            if (locked) {
                return;
            }
            isDraggingRef.current = false;
            const { width, height } = getContainerSize();
            // Clamp against the visible height so a minimized panel can be
            // dragged anywhere along the container, not just within the stored
            // full-size window's footprint.
            const visualSize = getVisualSize();
            const clamped = clampToContainer(d.x + VIEWER_INSET, d.y + VIEWER_INSET, width, height, visualSize);
            setCurrentPos(clamped);
            const metrics = computeEdgeMetrics(clamped, { width, height }, visualSize);
            rightGapRef.current = metrics.rightGap;
            bottomGapRef.current = metrics.bottomGap;
            horizontalAnchorRef.current = metrics.horizontalAnchor;
            verticalAnchorRef.current = metrics.verticalAnchor;
        },
        [clampToContainer, getContainerSize, getVisualSize, locked],
    );

    const onResizeStop = useCallback(
        (_e: unknown, _dir: unknown, elementRefArg: HTMLElement, _delta: unknown, pos: { x: number; y: number }) => {
            if (locked) {
                return;
            }
            const nextW = Math.max(elementRefArg.offsetWidth, minWidth);
            const nextH = Math.max(elementRefArg.offsetHeight, minHeight);
            const newSize: Size = { width: nextW, height: nextH };
            currentSizeRef.current = newSize;
            setCurrentSize(newSize);
            const { width: cw, height: ch } = getContainerSize();
            const clamped = clampToContainer(pos.x + VIEWER_INSET, pos.y + VIEWER_INSET, cw, ch, newSize);
            setCurrentPos(clamped);
            const metrics = computeEdgeMetrics(clamped, { width: cw, height: ch }, newSize);
            rightGapRef.current = metrics.rightGap;
            bottomGapRef.current = metrics.bottomGap;
            horizontalAnchorRef.current = metrics.horizontalAnchor;
            verticalAnchorRef.current = metrics.verticalAnchor;
        },
        [clampToContainer, getContainerSize, minWidth, minHeight, locked],
    );

    const outputPos = locked ? windowPos : currentPos;
    const outputSize = locked ? effectiveSize : currentSize;

    return {
        position: { x: outputPos.x - VIEWER_INSET, y: outputPos.y - VIEWER_INSET },
        size: { width: outputSize.width, height: autoHeight ? "auto" : outputSize.height },
        minWidth,
        minHeight: autoHeight ? undefined : minHeight,
        onDragStart,
        onDragStop,
        onResizeStop,
        setRndRef: locked ? () => undefined : setRndRef,
    };
}
